import { FaqSchema } from "@/components/FaqSchema";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";

const html = `<nav aria-label="Kruimelpad" class="px-3 pt-4 pb-2"><div class="container mx-auto max-w-6xl"><ol class="flex flex-wrap items-center gap-2 text-sm"><li class="flex items-center gap-2"><a href="/" class="text-muted-foreground hover:text-foreground transition-colors">Home</a></li><li class="text-foreground font-medium" aria-current="page">Wat is tossen?</li></ol></div></nav>

  <header class="py-10 md:py-20 px-3">
    <div class="container mx-auto max-w-3xl">
      <h1 class="text-3xl md:text-5xl font-bold text-foreground mb-6">Wat is tossen bij tennis?</h1>
      <p class="text-lg md:text-xl text-muted-foreground leading-relaxed"><strong>Tossen</strong> komt van het Engelse <em>to toss</em> ("opgooien" of "loten") en wordt op tennisclubs gebruikt voor een speelvorm waarbij spelers per ronde door loting aan elkaar gekoppeld worden. Een tossavond is in de praktijk vrijwel hetzelfde als een husselavond.</p>
    </div>
  </header>

  <article class="px-3 pb-6">
    <div class="container mx-auto max-w-3xl space-y-8">
      
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Tossen: twee betekenissen</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>"Tossen" wordt bij tennis in twee betekenissen gebruikt:</p><ol class="list-decimal pl-6 space-y-1"><li><strong>Voor een wedstrijd:</strong> spelers tossen met een racket of munt om te bepalen wie mag opslaan of welke speelhelft je kiest.</li><li><strong>Op de club:</strong> een tossavond of tossochtend is een vrij-speel-moment waarbij spelers door loting aan elkaar gekoppeld worden — meestal dubbelspel, 30 minuten per ronde.</li></ol></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Hoe verloopt een tossavond?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>Een typische tossavond:</p><ol class="list-decimal pl-6 space-y-1"><li>De club houdt banen vrij voor het tossmoment.</li><li>Leden melden zich aan — vooraf of aan de bar.</li><li>Een tossleider deelt spelers in over de banen (vroeger letterlijk met loting).</li><li>Iedere 30 minuten wordt opnieuw getoss(t).</li><li>Aan het einde napraten in de kantine.</li></ol></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Tossen, husselen, racket trekken — verschillen?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>Op de meeste clubs zijn de termen inwisselbaar. Strikt genomen verwijst <em>tossen</em> naar loting, <em>husselen</em> naar mixen en <em>racket trekken</em> naar de fysieke rackets-uit-een-mand-handeling. Maar in de praktijk doelt iedereen op hetzelfde: een wisselende-partner-speelvorm voor vrij spelen. <a href="/husselen-vs-tossen-vs-racket-trekken" class="text-accent hover:underline">Bekijk hier het volledige verschil</a>.</p></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Waarom kiezen clubs voor tossen?</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><ul class="list-disc pl-6 space-y-1"><li>Geen verplichting tot teamspel.</li><li>Sociale binding tussen leden.</li><li>Spelers leren elkaar over verschillende niveaus heen kennen.</li><li>Laagdrempelig: aanmelden, spelen, klaar.</li></ul></div>
        </section>
        <section>
          <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Tossavond automatiseren</h2>
          <div class="text-muted-foreground leading-relaxed space-y-4"><p>Tossavonden worden traditioneel door een vrijwilliger geleid. Dat kost tijd, en bij elke wisseling moeten spelers terug naar het clubhuis. <a href="/tossavond-organiseren" class="text-accent hover:underline">Met De Hussel App regel je de toss automatisch</a> — indeling, push-meldingen en aanmelden lopen via de telefoon van je leden.</p></div>
        </section>
    </div>
  </article>

  <section class="py-10 md:py-16 px-3">
    <div class="container mx-auto max-w-3xl">
      <div class="p-5 md:p-10 text-center rounded-lg border border-accent/30 bg-accent/10">
        <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-4">Klaar om dit voor je club te automatiseren?</h2>
        <p class="text-lg text-muted-foreground mb-6">De Hussel App regelt aanmelden, indelen en push-meldingen — voor husselen, tossen en racket trekken.</p>
        <a href="/club-register" class="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Registreer je club </a>
      </div>
    </div>
  </section>

  
  <section class="py-10 md:py-16 px-3">
    <div class="container mx-auto max-w-4xl">
      <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8 text-center">Veelgestelde vragen</h2>
      <div class="space-y-3">
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Wat betekent tossen bij tennis?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Tossen betekent "loten" — spelers worden door loting (of door een algoritme) aan elkaar gekoppeld voor een ronde dubbelspel. Op clubs spreekt men van een tossavond of tossochtend.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Wat is het verschil tussen tossen en husselen?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">In de praktijk vrijwel niets. Beide woorden duiden op een speelvorm waarbij spelers wisselend gekoppeld worden. De keuze hangt vaak af van regionale gewoonte op de club.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Hoe organiseer je een tossavond?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Reserveer banen, maak een sessie aan, laat leden zich aanmelden en deel ze in. Steeds meer clubs gebruiken hiervoor De Hussel App, die het indelen volledig automatiseert.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Hoe lang duurt een tossronde?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Standaard 30 minuten per ronde. Sommige clubs draaien 25 of 45 minuten — De Hussel App ondersteunt elke rondetijd tussen 20 en 60 minuten.</div>
    </details>
    <details class="group rounded-lg border border-border bg-card p-4 md:p-5">
      <summary class="flex items-center justify-between gap-3 cursor-pointer list-none">
        <h3 class="text-base md:text-lg font-semibold text-foreground">Kan een tossavond ook overdag?</h3>
        
      </summary>
      <div class="mt-3 text-muted-foreground leading-relaxed">Ja. Veel clubs draaien tossochtenden of -middagen, vooral voor senioren of niet-werkenden. Het concept is identiek aan een tossavond.</div>
    </details></div>
    </div>
  </section>`;

const WatIsTossen = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <ContentPageNav showPricing />
      <div
        className="flex-1 marketing-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <FaqSchema html={html} />
      <Footer />
    </div>
  );
};

export default WatIsTossen;
